import { LEAGUE_BULLION_BATTLE_STAKE, LEAGUE_GAME_KEYS, LEAGUE_GAME_META, PRIZE_COINS_PER_RUBY } from "./constants";
import {
    leaguePlayOpenLivePoints,
    leaguePlaySettledPoints,
    leagueSquaresOpenLiveReturn,
    leagueTraderWonPoints,
} from "./leaguePlays";
import type { League, LeagueGameKey, LeaguePlay, LeagueStatus } from "./types";

export {
    leagueBullionBattleEntryBlock,
    type LeagueBullionBattleEntryBlockReason,
} from "./leagueBullionEntryBlock";

/** Re-export: free by default (0). See `constants.ts`. */
export { LEAGUE_BULLION_BATTLE_STAKE };

/** Same order as `LEAGUE_GAME_KEYS` — Squares, Sixes, Bullion, Trader. */
export function leagueLobbyCardOrder(games: LeagueGameKey[]): LeagueGameKey[] {
    const set = new Set(games);
    const ordered = LEAGUE_GAME_KEYS.filter((game) => set.has(game));
    const rest = games.filter((game) => !LEAGUE_GAME_KEYS.includes(game));
    return [...ordered, ...rest];
}

/** One lobby section per game so each card is easy to spot in the Play tab. */
export type LeagueLobbySection = {
    id: LeagueGameKey;
    label: string;
    games: LeagueGameKey[];
};

export const LEAGUE_LOBBY_SECTIONS: LeagueLobbySection[] = LEAGUE_GAME_KEYS.map((game) => ({
    id: game,
    label: LEAGUE_GAME_META[game].label,
    games: [game],
}));

/** Included games grouped into lobby sections (empty sections omitted). */
export function leagueLobbyGamesBySection(
    games: LeagueGameKey[],
): { section: LeagueLobbySection; games: LeagueGameKey[] }[] {
    const ordered = leagueLobbyCardOrder(games);
    const set = new Set(ordered);
    return LEAGUE_LOBBY_SECTIONS
        .map((section) => ({
            section,
            games: section.games.filter((game) => set.has(game)),
        }))
        .filter((entry) => entry.games.length > 0);
}

/**
 * When each game is playable inside a match league.
 * All four games: prematch + live (same as the real lobbies — Squares Over 1 is open before kickoff).
 */
export function leagueGameIsPlayable(status: LeagueStatus, _game: LeagueGameKey): boolean {
    if (status === "ended") return false;
    return status === "upcoming" || status === "live";
}

/**
 * Finished leagues stay viewable: open Sixes / Bullion / Trader / Squares read-only
 * (boards, leaderboards, settled picks). Backend still rejects new stakes.
 */
export function leagueGameIsViewable(status: LeagueStatus, _game: LeagueGameKey): boolean {
    if (status === "ended") return true;
    return leagueGameIsPlayable(status, _game);
}

/** Players who have opened or settled a Bullion play in this league. */
export function leagueBullionEnteredUserIds(league: League): number[] {
    const ids = new Set<number>(league.bullionBattle?.enteredUserIds ?? []);
    league.members.forEach((member) => {
        if ((member.byGame.bullion?.played ?? 0) > 0) ids.add(member.userId);
    });
    league.plays.forEach((play) => {
        if (play.game === "bullion") ids.add(play.userId);
    });
    return [...ids];
}

export function leagueBullionEnteredCount(league: League): number {
    return leagueBullionEnteredUserIds(league).length;
}

/** Place-bonus points added for every 9 of pool weight. Mirrors backend settle math. */
export const LEAGUE_BULLION_POINTS_PER_NINE = 1500;

/**
 * League Bullion+ place-bonus pool: ceil(weight / 9) * 1500.
 * Weight is real players, or players + extra Rubies. One to nine share a 1,500 band.
 */
export function leagueBullionPointsPoolSize(playerCount: number): number {
    const n = Math.floor(Number(playerCount));
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.ceil(n / 9) * LEAGUE_BULLION_POINTS_PER_NINE;
}

/** Extra Rubies that grew the coin pot after entry (seat top-ups + Sixes player-switch). */
export function leagueExtraRebuyRubies(league: {
    prizePotCoins?: number | null;
    entryFeeRubies: number;
    members: Array<{ userId: number }>;
}): number {
    const seats = league.members.length;
    const stored = league.prizePotCoins;
    const pot =
        stored != null && Number.isFinite(Number(stored))
            ? Math.max(0, Math.round(Number(stored)))
            : Math.max(0, Math.round(league.entryFeeRubies)) * seats * PRIZE_COINS_PER_RUBY;
    const unit = Math.max(1, PRIZE_COINS_PER_RUBY);
    const potUnits = Math.floor(pot / unit);
    const entryUnits = Math.max(0, Math.round(league.entryFeeRubies)) * seats;
    return Math.max(0, potUnits - entryUnits);
}

export function leagueBullionPoolWeight(playerCount: number, extraRubies = 0): number {
    const p = Math.max(0, Math.floor(Number(playerCount)) || 0);
    const e = Math.max(0, Math.floor(Number(extraRubies)) || 0);
    return p + e;
}

/**
 * Max place-bonus one entrant can win (1st-place share of the bonus pool).
 * Plan uses real player count; pool size uses `poolWeight`.
 */
export function leagueBullionPointsMaxShare(playerCount: number, poolWeight: number = playerCount): number {
    const pool = leagueBullionPointsPoolSize(poolWeight);
    if (pool <= 0) return 0;
    const n = Math.floor(Number(playerCount));
    if (!Number.isFinite(n) || n <= 9) return pool;
    if (n <= 30) return Math.round(pool * 0.5);
    return Math.round(pool * 0.38);
}

/** Roster rows for people in the shared battle — viewer first when they have entered. */
export function leagueBullionEnteredPeople(
    league: League,
    viewerUserId?: number,
): Array<Pick<League["members"][number], "userId" | "playerName" | "avatar">> {
    const entered = new Set(leagueBullionEnteredUserIds(league));
    const members = league.members.filter((member) => entered.has(member.userId));
    if (viewerUserId == null) return members;
    const viewer = members.find((member) => member.userId === viewerUserId);
    if (!viewer) return members;
    return [viewer, ...members.filter((member) => member.userId !== viewerUserId)];
}

/** True when this user has already entered the league’s shared Bullion battle. */
export function leagueViewerInBullionBattle(league: League, userId: number): boolean {
    if (league.bullionBattle?.viewerEntered) return true;
    if (league.bullionBattle?.enteredUserIds?.includes(userId)) return true;
    return league.plays.some((play) => play.game === "bullion" && play.userId === userId);
}

export function leagueBullionBattleName(league: Pick<League, "name">): string {
    const base = league.name.trim() || "League";
    const short = base.length > 28 ? `${base.slice(0, 26)}…` : base;
    return `${short} Battle`;
}

/** Viewer's Trader pts won in this league — full stake×odds return; losses count 0. */
export function leagueViewerTraderPl(league: League): number {
    const me = league.members.find((member) => member.isViewer);
    if (!me) return 0;
    const settled = league.plays.filter(
        (play) => play.game === "trader" && play.userId === me.userId && play.status === "settled",
    );
    if (settled.length > 0) {
        return settled.reduce((sum, play) => sum + leagueTraderWonPoints(play), 0);
    }
    return Math.max(0, Math.round(Number(me.byGame.trader?.pl) || 0));
}

/**
 * Viewer's Super Squares points — chip return per over (same as leaderboard / Activity),
 * not net P/L (`SUM(delta)`). Losing overs that still scored chips count those chips; total
 * losses do not drag the sum negative.
 */
export function leagueViewerSquaresPoints(league: League, viewerUserId?: number): number {
    const uid =
        viewerUserId ??
        league.members.find((member) => member.isViewer)?.userId;
    if (uid == null) return 0;

    let sum = 0;
    let sawPlay = false;
    for (const play of league.plays || []) {
        if (play.game !== "squares" || play.userId !== uid) continue;
        sawPlay = true;
        if (play.status === "open") {
            const live = leagueSquaresOpenLiveReturn(play);
            if (live != null) sum += live;
        } else {
            sum += leaguePlaySettledPoints(play);
        }
    }
    if (sawPlay) return Math.max(0, Math.round(sum));

    // Seed / settlement-only fallback when plays aren't hydrated.
    for (const entry of league.settlements || []) {
        if (entry.game !== "squares" || entry.userId !== uid) continue;
        sum += leaguePlaySettledPoints({
            game: "squares",
            stake: entry.stake,
            delta: entry.delta,
            status: "settled",
            outcome: entry.outcome,
            marketDetails: entry.marketDetails,
        });
    }
    if (sum > 0) return Math.max(0, Math.round(sum));

    const me = league.members.find((member) => member.userId === uid);
    return Math.max(0, Math.round(Number(me?.byGame.squares?.pl) || 0));
}

/** Viewer's Trader plays in this league (open/ACTIVE first, then newest first). */
export function leagueViewerTraderPlays(league: League): LeaguePlay[] {
    const me = league.members.find((member) => member.isViewer);
    if (!me) return [];
    return league.plays
        .filter((play) => play.game === "trader" && play.userId === me.userId)
        .sort((a, b) => {
            const aOpen = a.status === "open" ? 1 : 0;
            const bOpen = b.status === "open" ? 1 : 0;
            if (aOpen !== bOpen) return bOpen - aOpen;
            return new Date(b.placedAt).getTime() - new Date(a.placedAt).getTime();
        });
}

/** Live (open) or settled points for one Sixes / Bullion play; null when the play carries no score yet. */
function leaguePointsModelPlayPoints(play: LeaguePlay): number | null {
    if (play.status === "open") return leaguePlayOpenLivePoints(play);
    return leaguePlaySettledPoints(play);
}

/**
 * Viewer's Sixes / Bullion points from their plays — live card score / slip score while open,
 * final score after settle. Null when no play has a score (caller falls back to `byGame`).
 */
function leagueViewerPointsModelPoints(league: League, game: "sixes" | "bullion", userId?: number): number | null {
    if (userId == null) return null;
    let sum = 0;
    let scored = false;
    for (const play of league.plays) {
        if (play.game !== game || play.userId !== userId) continue;
        const points = leaguePointsModelPlayPoints(play);
        if (points == null) continue;
        sum += points;
        scored = true;
    }
    if (scored) return sum;
    if (game === "bullion") {
        const settlement = league.bullionBattle?.viewerSlip?.settlement;
        const slip = Number(settlement?.slipScore ?? settlement?.points);
        if (Number.isFinite(slip)) return Math.max(0, Math.round(slip));
    }
    return null;
}

/** Live Sixes footer counts from linked cards (`wins_count` / `slides_count` / `natural_count`). */
function leagueViewerSixesCardCounts(
    league: League,
    userId?: number,
): { wins: number; slides: number; natural: number } | null {
    if (userId == null) return null;
    let wins = 0;
    let slides = 0;
    let natural = 0;
    let seen = false;
    for (const play of league.plays) {
        if (play.game !== "sixes" || play.userId !== userId) continue;
        const md = play.marketDetails;
        const cardWins = Number(md?.winsCount);
        const cardSlides = Number(md?.slidesCount);
        const cardNatural = Number(md?.naturalCount);
        if (!Number.isFinite(cardWins) && !Number.isFinite(cardSlides) && !Number.isFinite(cardNatural)) {
            continue;
        }
        seen = true;
        if (Number.isFinite(cardWins)) wins += Math.max(0, Math.round(cardWins));
        if (Number.isFinite(cardSlides)) slides += Math.max(0, Math.round(cardSlides));
        if (Number.isFinite(cardNatural)) natural += Math.max(0, Math.round(cardNatural));
    }
    return seen ? { wins, slides, natural } : null;
}

/** Viewer's winning Bullion picks on the graded battle slip. Null when there is no slip. */
function leagueViewerBullionWins(league: League): number | null {
    const picks = league.bullionBattle?.viewerSlip?.picks;
    if (!picks || typeof picks !== "object" || Object.keys(picks).length === 0) return null;
    let wins = 0;
    for (const pick of Object.values(picks)) {
        if (!pick || typeof pick !== "object") continue;
        const outcome = String((pick as { settlementOutcome?: unknown }).settlementOutcome ?? "")
            .trim()
            .toLowerCase();
        if (outcome === "win") wins += 1;
    }
    return wins;
}

type BullionBattleRankRow = {
    userId: number;
    points: number;
    potentialPoints: number;
    rank: number | null;
};

type BullionSettlementBits = {
    points?: unknown;
    slipScore?: unknown;
    potentialPoints?: unknown;
    rank?: unknown;
};

function bullionSlipScoreFromSettlement(settlement: BullionSettlementBits | null | undefined): {
    points: number;
    potentialPoints: number;
    rank: number | null;
} {
    if (!settlement) return { points: 0, potentialPoints: 0, rank: null };
    const slip =
        settlement.slipScore != null && Number.isFinite(Number(settlement.slipScore))
            ? Math.round(Number(settlement.slipScore))
            : Math.round(Number(settlement.points) || 0);
    return {
        points: Math.max(0, slip),
        potentialPoints: Math.max(0, Math.round(Number(settlement.potentialPoints) || 0)),
        rank:
            settlement.rank != null && Number.isFinite(Number(settlement.rank)) && Number(settlement.rank) > 0
                ? Number(settlement.rank)
                : null,
    };
}

/** Merge entrant / play / viewer-slip scores so RANK tracks the same live points as the battle board. */
function bullionBattleScoreForUser(
    league: League,
    userId: number,
    viewerUserId: number,
): BullionBattleRankRow {
    const battle = league.bullionBattle;
    const entrant = (battle?.entrants ?? []).find((e) => Number(e.userId) === Number(userId));
    const fromEntrant = entrant
        ? {
              points:
                  entrant.slipScore != null && Number.isFinite(Number(entrant.slipScore))
                      ? Math.max(0, Math.round(Number(entrant.slipScore)))
                      : Math.max(0, Math.round(Number(entrant.points) || 0)),
              potentialPoints: Math.max(0, Math.round(Number(entrant.potentialPoints) || 0)),
              rank:
                  entrant.rank != null && Number.isFinite(Number(entrant.rank)) && Number(entrant.rank) > 0
                      ? Number(entrant.rank)
                      : null,
          }
        : null;

    const play = league.plays.find(
        (p) => p.game === "bullion" && Number(p.userId) === Number(userId),
    );
    const fromPlay = bullionSlipScoreFromSettlement(
        play?.marketDetails?.settlement as BullionSettlementBits | undefined,
    );

    const fromViewerSlip =
        Number(userId) === Number(viewerUserId)
            ? bullionSlipScoreFromSettlement(
                  battle?.viewerSlip?.settlement as BullionSettlementBits | undefined,
              )
            : null;

    const points = Math.max(
        fromEntrant?.points ?? 0,
        fromPlay.points,
        fromViewerSlip?.points ?? 0,
    );
    const potentialPoints = Math.max(
        fromEntrant?.potentialPoints ?? 0,
        fromPlay.potentialPoints,
        fromViewerSlip?.potentialPoints ?? 0,
    );
    const rank = fromEntrant?.rank ?? fromPlay.rank ?? fromViewerSlip?.rank ?? null;
    return { userId, points, potentialPoints, rank };
}

/**
 * Viewer's position on the in-battle Bullion leaderboard.
 * Null until any entrant has slip points (same moment the battle board starts showing POS.).
 */
function leagueViewerBullionBattleRank(league: League, userId?: number): number | null {
    if (userId == null) return null;
    const battle = league.bullionBattle;
    if (!battle) return null;

    const idSet = new Set<number>();
    for (const e of battle.entrants ?? []) idSet.add(Number(e.userId));
    for (const id of battle.enteredUserIds ?? []) idSet.add(Number(id));
    for (const play of league.plays) {
        if (play.game === "bullion") idSet.add(Number(play.userId));
    }
    if (idSet.size === 0 || !idSet.has(Number(userId))) return null;

    const rows = [...idSet].map((uid) => bullionBattleScoreForUser(league, uid, userId));

    // Show RANK only once someone on the battle board has points.
    if (!rows.some((row) => row.points > 0)) return null;

    const mine = rows.find((row) => Number(row.userId) === Number(userId));
    if (mine?.rank != null && mine.rank > 0) return Math.round(mine.rank);

    const sorted = [...rows].sort((a, b) => {
        if (a.rank != null && a.rank > 0 && b.rank != null && b.rank > 0 && a.rank !== b.rank) {
            return a.rank - b.rank;
        }
        if (b.points !== a.points) return b.points - a.points;
        if (b.potentialPoints !== a.potentialPoints) return b.potentialPoints - a.potentialPoints;
        return a.userId - b.userId;
    });

    // Competition ranks (1, 1, 3…) when slip score + potential tie.
    let lastPts: number | null = null;
    let lastPot: number | null = null;
    let displayRank = 0;
    for (let i = 0; i < sorted.length; i += 1) {
        const row = sorted[i]!;
        if (lastPts === null || row.points !== lastPts || row.potentialPoints !== lastPot) {
            displayRank = i + 1;
            lastPts = row.points;
            lastPot = row.potentialPoints;
        }
        if (Number(row.userId) === Number(userId)) return displayRank;
    }
    return null;
}

/** Play-tab stat chips (counts + points for the active game). */
export type LeaguePlayPanelStat = { label: string; value: number | string };

export function leagueViewerPlayPanelStats(
    league: League,
    game: LeagueGameKey,
): LeaguePlayPanelStat[] {
    const me = league.members.find((member) => member.isViewer);
    const stats = me?.byGame[game] ?? { pl: 0, wins: 0, losses: 0, played: 0 };
    const myPlays = league.plays.filter(
        (play) => play.game === game && (me == null || play.userId === me.userId),
    );
    const sixesCounts = game === "sixes" ? leagueViewerSixesCardCounts(league, me?.userId) : null;
    const liveWins =
        game === "sixes"
            ? (sixesCounts?.wins ?? null)
            : game === "bullion"
              ? leagueViewerBullionWins(league)
              : null;
    const wins = Math.max(0, Math.round(liveWins ?? (Number(stats.wins) || 0)));
    const points = Math.max(
        0,
        Math.round(
            game === "trader"
                ? leagueViewerTraderPl(league)
                : game === "squares"
                  ? leagueViewerSquaresPoints(league, me?.userId)
                  : (leagueViewerPointsModelPoints(league, game, me?.userId) ?? (Number(stats.pl) || 0)),
        ),
    );

    if (game === "trader") {
        return [
            { label: "Trades", value: myPlays.length || Math.round(Number(stats.played) || 0) },
            { label: "Wins", value: wins },
            { label: "Points", value: points },
        ];
    }
    if (game === "squares") {
        return [
            { label: "Overs", value: Math.round(Number(stats.played) || 0) || myPlays.length },
            { label: "Wins", value: wins },
            { label: "Points", value: points },
        ];
    }
    if (game === "sixes") {
        const slides = Math.max(0, Math.round(sixesCounts?.slides ?? 0));
        const natural = Math.max(0, Math.round(sixesCounts?.natural ?? 0));
        return [
            { label: "Correct", value: wins },
            { label: "Slides", value: slides },
            { label: "Natural", value: natural },
            { label: "Points", value: points },
        ];
    }
    const pickCount = (() => {
        const battle = league.bullionBattle;
        const slipPicks = battle?.viewerSlip?.picks;
        if (slipPicks && typeof slipPicks === "object" && Object.keys(slipPicks).length > 0) {
            return Object.keys(slipPicks).length;
        }
        const saved = me != null ? battle?.viewerPicksByUserId?.[String(me.userId)] : null;
        const selections = saved?.selections;
        if (selections && typeof selections === "object" && Object.keys(selections).length > 0) {
            return Object.keys(selections).length;
        }
        return Math.round(Number(stats.played) || 0) || myPlays.length;
    })();
    const rank = leagueViewerBullionBattleRank(league, me?.userId);
    return [
        { label: "Picks", value: pickCount },
        { label: "Wins", value: wins },
        { label: "Points", value: points },
        { label: "Rank", value: rank != null && rank > 0 ? rank : "—" },
    ];
}

/** Seeded match-winner odds when the live feed isn't attached yet (prototype / prematch). */
export function leagueTraderDemoWinnerOdds(
    league: League,
): { home: number; away: number } {
    let seed = 2166136261;
    const key = `${league.id}:trader-mw:${league.fixture?.id ?? ""}`;
    for (let i = 0; i < key.length; i += 1) {
        seed ^= key.charCodeAt(i);
        seed = Math.imul(seed, 16777619);
    }
    seed >>>= 0;
    const next = () => {
        seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
        return seed;
    };
    const home = Math.round((1.55 + (next() % 90) / 100) * 100) / 100;
    const away = Math.round((1.55 + (next() % 90) / 100) * 100) / 100;
    return { home, away };
}

